import { ProductStatus, productVersionToFirestore } from '@parallel-fluidics/shared/models/products';
import {
  doc,
  getFirestore,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { isEqual } from 'lodash';

function comparableReorderPolicy(policy) {
  if (!policy) return null;
  const rest = { ...policy };
  delete rest.updatedAt;
  return rest;
}

const MANUFACTURING_FIELDS = [
  'projectId',
  'projectDocId',
  'pPartId',
  'pPartDocId',
  'unitPrice',
  'leadtimeDays',
];

// The form represents missing values as '' while Firestore stores null (or omits
// the field entirely), so treat all three as equal when diffing.
function comparableFieldValue(value) {
  if (value === '' || value === undefined) return null;
  return value;
}

export default async function updateProductVersion({
  productDocId,
  versionId,
  versionData,
}) {
  const db = getFirestore();
  const productRef = doc(db, 'products', productDocId);
  const versionRef = doc(db, 'products', productDocId, 'versions', versionId);

  await runTransaction(db, async (transaction) => {
    const [productSnapshot, versionSnapshot] = await Promise.all([
      transaction.get(productRef),
      transaction.get(versionRef),
    ]);

    if (!productSnapshot.exists()) {
      throw new Error(`Product not found: ${productDocId}`);
    }

    if (!versionSnapshot.exists()) {
      throw new Error(`Version not found: ${versionId}`);
    }

    const existingVersion = versionSnapshot.data();

    const canEditManufacturingFields = existingVersion.status === ProductStatus.DRAFT;

    if (!canEditManufacturingFields) {
      const modifiedFields = MANUFACTURING_FIELDS.filter((field) => (
        comparableFieldValue(versionData[field]) !== comparableFieldValue(existingVersion[field])
      ));
      if (modifiedFields.length > 0) {
        throw new Error(
          `Manufacturing fields can only be edited on draft versions. Modified: ${modifiedFields.join(', ')}`,
        );
      }
    }

    const versionDoc = productVersionToFirestore({
      status: existingVersion.status || ProductStatus.DRAFT,
      projectId: versionData.projectId,
      projectDocId: versionData.projectDocId || null,
      pPartId: versionData.pPartId,
      pPartDocId: versionData.pPartDocId || null,
      unitPrice: versionData.unitPrice,
      leadtimeDays: versionData.leadtimeDays ?? null,
      previewGlb: versionData.previewGlb || null,
      reorderPolicy: versionData.reorderPolicy ?? existingVersion.reorderPolicy ?? null,
      effectiveAt: existingVersion.effectiveAt || null,
      retiredAt: existingVersion.retiredAt || null,
      createdAt: existingVersion.createdAt,
      createdBy: existingVersion.createdBy,
    }, serverTimestamp);

    if (versionDoc.reorderPolicy) {
      const policyChanged = !isEqual(
        comparableReorderPolicy(existingVersion.reorderPolicy),
        comparableReorderPolicy(versionDoc.reorderPolicy),
      );
      versionDoc.reorderPolicy.updatedAt = policyChanged
        ? serverTimestamp()
        : (existingVersion.reorderPolicy?.updatedAt ?? null);
    }

    transaction.set(versionRef, versionDoc);
  });
}
